import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FinancialService } from '../../../../core/data-services/company';
import { distinctUntilChanged, debounceTime, switchMap } from 'rxjs/operators';
import { CompanyBasic, Position } from '../../../models';
import { Subject } from 'rxjs';
import { AutocompleteService } from '../../../../core/data-services';
import { AuthorizationService, FinancialUtilsService } from '../../../../core/services';
import { PERMISSIONS } from '../../../enums/permissions.enum';
import { NgSelectComponent } from '@ng-select/ng-select';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PortfolioOptixSaveModalComponent } from '../portfolio-optix-save-modal/portfolio-optix-save-modal.component';
import { RmsColors } from '../../../constants/rms-colors';

const missingValue = '-';
@Component({
    selector: 'rms-portfolio-optix-table',
    templateUrl: './portfolio-optix-table.component.html',
    styleUrls: ['./portfolio-optix-table.component.scss'],
    standalone: false
})
export class PortfolioOptixTableComponent implements OnInit {

  @ViewChild('ngSelectComponent', { static: false }) ngSelectComponent: NgSelectComponent;

  @Input() isPortfolioTable: boolean;
  @Input() selectedItems: Array<Position> = [];
  @Input() isPortfolio: (CompanyBasic) => boolean;
  @Output() onRemoveAll = new EventEmitter();
  @Output() onRemoveOne = new EventEmitter<Position>();
  @Output() onValueChange = new EventEmitter<{
    item: Position,
    newValue: number,
    type: string,
    currentValues: {'fv': number, 'dfv': number, 'pos': number}
  }>();
  @Output() onAddCompany = new EventEmitter<Array<CompanyBasic>>();
  @Output() onEdit = new EventEmitter<{isPortfolio: boolean, ids: Array<string>}>();

  public companiesTypeahead = new Subject<string>();
  public hasPermissions: Record<string, boolean> = {};
  public FinancialUtilsService = FinancialUtilsService;
  public autocompleteItems: Array<CompanyBasic> = [];
  public selectedSearchItem: any;

  public editedRows: Record<number, {fv: number, dfv: number, pos: number}> = {};

  public readonly RMS_TEAL_COLOR = RmsColors.RMS_TEAL_COLOR;

  constructor(
    private authorizationService: AuthorizationService,
    private financialService: FinancialService,
    private autocompleteService: AutocompleteService,
    private cd: ChangeDetectorRef,
    private modalService: NgbModal,
  ) {
    this.hasPermissions = {
      coreCompanyFinancialUpdate: this.authorizationService.hasPermission([PERMISSIONS.CoreCompanyFinancialUpdate]),
      corePriceTargetUpdate: this.authorizationService.hasPermission([PERMISSIONS.CorePriceTargetUpdate]),
    };
  }

  ngOnInit() {
    this.companiesAutocomplete();
  }

  public isEditing(item): boolean {
    return item.company.id in this.editedRows;
  }

  public edit(item) {
    this.editedRows[item.company.id] = {
      fv: item.fairValue,
      dfv: item.downsideFairValue,
      pos: item.companyFinancial.probabilityOfSuccess
    };
    this.onEdit.emit({isPortfolio: this.isPortfolioTable, ids: Object.keys(this.editedRows) });
  }

  public cancelEdit(item) {
    this.handleEditedValueReset(item);
    this.removeItemFromEdited(item);
  }


  public unselect(item: Position) {
    this.onRemoveOne.emit(item);
  }

  public unselectAll() {
    this.onRemoveAll.emit();
  }

  public openConfirmModal(item) {
    if (!this.hasDirtyValues(item)) {
      return;
    }
    const newValue =  this.editedRows[item.company.id];

    const modalRef = this.modalService.open(PortfolioOptixSaveModalComponent, {
      centered: true,
    });
    modalRef.componentInstance.item = item;
    modalRef.componentInstance.newValues = this.getChangedValues(item);
    modalRef.result.then((result) => {
      if (result) {
       this.editFinancials(item);
      }
    });
  }

  public editFinancials(item) {
    const companyId = item.company.id;
    const newValues = this.editedRows[item.company.id];
    const body = {
      ...item.companyFinancial,
      priceTarget: {
        long: item.companyFinancial.longPrice,
        fair: newValues.fv,
        short: item.companyFinancial.shortPrice,
        downsideFair: newValues.dfv
      },
      probabilityOfSuccess: newValues.pos
    };
    this.financialService.updateCompanyFinancial(companyId, body).subscribe(() => {
      item.companyFinancial.probabilityOfSuccess = newValues.pos;
      item.downsideFairValue = newValues.dfv;
      item.fairValue = newValues.fv;
      this.removeItemFromEdited(item);
      if (!this.isPortfolioTable) {
        this.onAddCompany.emit(item.company);
      }
    });
  }

  public changeFairValue(event: Event, item) {
    const input = event.target as HTMLInputElement;
    const newValue = +input.value;
    item.companyFinancial.useCalculatedFairValue = false;
    this.onValueChange.emit({item, newValue, type: 'FV', currentValues:  this.editedRows[item.company.id]});
  }

  public changeDownsideFairValue(event: Event, item) {
    const input = event.target as HTMLInputElement;
    const newValue = +input.value;
    this.onValueChange.emit({item, newValue, type: 'DFV', currentValues:  this.editedRows[item.company.id]});
  }

  public changeProbabilityOfSuccess(event: Event, item) {
    const input = event.target as HTMLInputElement;
    const newValue = +input.value;
    this.onValueChange.emit({item, newValue, type: 'POS', currentValues:  this.editedRows[item.company.id]});
  }

  public hasMissingValues(item): boolean {
    return this.getDownSide(item) === missingValue || this.getUpside(item) === missingValue;
  }

  public getDownSide(item) {
    return item.plottedValue && !Number.isNaN(item.plottedValue[0]) ? item.plottedValue[0].toFixed(2) : missingValue;
  }

  public getUpside(item) {
    return item.plottedValue && !Number.isNaN(item.plottedValue[1]) ? item.plottedValue[1].toFixed(2) : missingValue;
  }

  public getProbabilityOfSuccess(item, newValue) {
    const { companyFinancial } = item;
    if (!companyFinancial) {
      return '-';
    }
    const { probabilityOfSuccess } = companyFinancial;
    const val = typeof newValue === 'undefined' ? probabilityOfSuccess : newValue;
    return (val || val === 0) && !Number.isNaN(val) ? val : missingValue;
  }

  public getDownsideFairValue(item, newValue) {
    const { downsideFairValue, currencyCode } = item;
    const val = typeof newValue === 'undefined' ? downsideFairValue : newValue;
    if (Number.isNaN(val)) {
      return '-';
    }
    return (val || val === 0) && currencyCode ? FinancialUtilsService.convertMoneyCurrency(val, currencyCode, 2) : missingValue;
  }

  public getFairValue(item, newValue) {
    const { fairValue, currencyCode } = item;
    const val = typeof newValue === 'undefined' ? fairValue : newValue;
    if (Number.isNaN(val)) {
      return '-';
    }
    return (val || val === 0) && currencyCode ? FinancialUtilsService.convertMoneyCurrency(val, currencyCode, 2) : missingValue;
  }

  public getLastTrade(item) {
    const { lastTrade, currencyCode } = item;
    if (Number.isNaN(lastTrade)) {
      return '-';
    }
    return lastTrade && currencyCode ? FinancialUtilsService.convertMoneyCurrency(lastTrade, currencyCode, 2) : missingValue;
  }

  public handleNonPortfolioChange(selectedCompanies) {
    this.onAddCompany.emit(selectedCompanies);
    this.ngSelectComponent.handleClearClick();
  }

  private removeItemFromEdited(item) {
    this.editedRows = Object.keys(this.editedRows)
      .filter(i => item.company.id !== +i)
      .reduce((acc, i) =>  ({...acc, [i]: this.editedRows[i]}), {});
    this.onEdit.emit({isPortfolio: this.isPortfolioTable, ids: Object.keys(this.editedRows) });
  }

  private hasDirtyValues(item) {
    const newValues = this.editedRows[item.company.id];
    return newValues['fv'] !== item.fairValue ||
      newValues['dfv'] !== item.downsideFairValue ||
      newValues['pos'] !== item.companyFinancial.probabilityOfSuccess;
  }

  private getChangedValues(item) {
    const changes = {};
    const newValues = this.editedRows[item.company.id];
    if (newValues['fv'] !== item.fairValue) {
      changes['fv'] = this.getFairValue(item, newValues['fv']);
    }
    if (newValues['dfv'] !== item.downsideFairValue) {
      changes['dfv'] = this.getDownsideFairValue(item, newValues['dfv']);
    }
    if (newValues['pos'] !== item.companyFinancial.probabilityOfSuccess) {
      changes['pos'] = this.getProbabilityOfSuccess(item, newValues['pos']);
    }
    return changes;
  }

  private handleEditedValueReset(item) {
    const newValues = this.editedRows[item.company.id];
    const currentValues = {
      fv: item.fairValue,
      dfv: item.downsideFairValue,
      pos: item.companyFinancial.probabilityOfSuccess
    };
    if (newValues['fv'] !== item.fairValue) {
      this.onValueChange.emit({item, newValue: item.fairValue, type: 'FV',
        currentValues});
    }
    if (newValues['dfv'] !== item.downsideFairValue) {
      this.onValueChange.emit({item, newValue: item.downsideFairValue, type: 'DFV',
        currentValues});
    }
    if (newValues['pos'] !== item.companyFinancial.probabilityOfSuccess) {
      this.onValueChange.emit({item, newValue: item.companyFinancial.probabilityOfSuccess, type: 'POS',
        currentValues});
    }
  }

  private companiesAutocomplete() {
    this.companiesTypeahead.pipe(
      distinctUntilChanged(),
      debounceTime(200),
      switchMap(term => this.autocompleteService.autocompleteEntity<CompanyBasic>('companies', {q: term })),
    ).subscribe(value => {
      this.autocompleteItems = value.records.map(c => ({ ...c, id: +c.id}))
        .filter(c => c && (this.isPortfolioTable ? this.isPortfolio(c) : !this.isPortfolio(c) ) &&
          !this.selectedItems.some(i => i.company.id === c.id));
      this.cd.markForCheck();
    });
  }
}
